import {
    Autocomplete,
    AutocompleteItem,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
    Modal,
    ModalContent,
    Button,
    ModalFooter,
    ModalBody,
    ModalHeader,
    Tabs, Tab, Card, CardBody
} from "@heroui/react";
import React, { useState, useMemo, useCallback } from "react";
import { Icon } from "@iconify/react";
import { apiClientExporter } from "../../services/apiClient";
import { saveAs } from "file-saver";
import DetalleQuickviewExporter from "./DetalleQuickviewExporter";
import DetalleQuickview from "../../components/claim/DetalleQuickView";
import DetalleMaestraEditar from "./DetalleMaestraEditar";
import DetalleJudicialEditar from "./DetalleJudicialEditar";
import ClaimLetterTab from "./ClaimLetterTab";
import AuditLogTab from "./AuditLogTab";
import { motion, AnimatePresence } from "framer-motion";
import { generateCopiableName } from "../../utils/general";
import { calcularDiasQueFaltan } from "../../utils/general/exporterUtil";
import { format } from "date-fns";

function ClaimsTable({
    tableData,
    profile,
    filterValue,
    setFilterValue,
    myCasesOnly,
    rowsPerPage,
    page,
    setPage,
    sortDescriptor,
    setSortDescriptor,
    topContent,
    bottomContent,
    filterMode = "OR",
    onCasesDataReload,
    onUpdateSingleCase,
    aCargoFilter = null, // Nuevo prop para filtrar por A Cargo
    aCargoOptions = [],
    readOnlyClaims = false,
    showNewEmailIndicators = false // Flag temporal para ocultar resaltado/ícono de correos nuevos
}) {
    //check if user has admin view (profile_id 2, 5 ,6)
    // 2 Rsk => admin
    // 5 Rsk_recovery
    // 6 Rsk_administración
    // 8 Rsk_hybrid
    const adminView = (profileid) => {
        //console.log("profiledata", profile);
        if (!profileid) {
            return false;
        } else {
            return profileid === 2 || profileid === 5 || profileid === 6;
        }
    }

    //MANEJO DE ESTADO PARA EDITAR MAESTRA
    const [maestraLoading, setMaestraLoading] = useState(false);
    const [maestraData, setMaestraData] = useState(null);
    const [maestraQvcId, setMaestraQvcId] = useState(null);
    const [isMaestraModalOpen, setIsMaestraModalOpen] = useState(false);
    const [selectedTab, setSelectedTab] = useState("maestra");

    //MANEJO DE ESTADO PARA CARTA DE RECLAMO
    const [isQuickViewModalOpen, setIsQuickViewModalOpen] = useState(false);
    const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
    const [selectedRskRef, setSelectedRskRef] = useState(null);
    const [setDocxBlob] = useState(null);
    const [pdfBlob, setPdfBlob] = useState(null);
    const [documentFilename, setDocumentFilename] = useState("");

    const newEmailIndicatorsEnabled = showNewEmailIndicators &&
        process.env.REACT_APP_SHOW_NEW_EMAIL_INDICATOR !== "false";

    const shouldHighlightNewEmail = (row) =>
        newEmailIndicatorsEnabled && row?.has_new_email;

    // Función para determinar el color basado en los días que faltan
    const getDiasColor = (diasQueFaltan) => {
        if (diasQueFaltan === "N/A" || diasQueFaltan === "Expired") {
            return "text-gray-500"; // Gris para N/A y Expired
        }

        const dias = parseInt(diasQueFaltan);
        if (isNaN(dias)) return "text-gray-500";

        if (dias <= 30) {
            return "text-red-600 font-bold"; // Rojo para 30 días o menos
        } else if (dias <= 60) {
            return "text-yellow-600 font-semibold"; // Amarillo para 31-60 días
        } else {
            return "text-green-600"; // Verde para más de 60 días
        }
    };

    // Función para determinar el color de fondo de la fila basado en mail_status
    const getRowBackgroundColor = (row) => {
        // Solo aplicar color azul para emails nuevos
        if (shouldHighlightNewEmail(row)) {
            return "bg-blue-200 hover:bg-blue-300"; // Azul más intenso
        }
        return ""; // Sin color especial
    };

    // Define table columns, including exporter_name for admin users
    const columns = useMemo(() => {
        const baseColumns = [
            { name: "RSK Ref.", uid: "rsk_ref", sortable: true },
            { name: "Status", uid: "status", sortable: true },
            { name: "A Cargo", uid: "a_cargo", sortable: true },
            { name: "Vessel", uid: "vessel", sortable: true },
            { name: "BL/AWB", uid: "bl_awb", sortable: true },
            { name: "Container", uid: "container_number", sortable: true },
            { name: "Carrier", uid: "carrier_name", sortable: true },
            { name: "Claim Amount", uid: "claimamount", sortable: true },
            { name: "Offer", uid: "ofertas_de_cierre", sortable: true }, // Ofertas de Cierre
            { name: "Recovered amount", uid: "recuperado_usd", sortable: true },
            { name: "Release", uid: "fecha_finiquito", sortable: true }, // Fecha Finiquito
            { name: "Actions", uid: "actions", sortable: false },
        ];
        if (adminView(profile?.profile_id)) { //2 admin, 5 recovery, 6 administracion
            return [
                baseColumns[0],
                { name: "Exporter name", uid: "exporter_name", sortable: true },
                { name: "Client", uid: "client_name", sortable: true },
                { name: "Remaining", uid: "time_bar", sortable: true },
                ...baseColumns.slice(1),
            ];
        }
        return baseColumns;
    }, [profile]);

    // Filter the table data based on search input, including exporter_name for admin users
    const filteredData = useMemo(() => {
        if (!tableData || !Array.isArray(tableData)) {
            return [];
        }

        let filtered = [...tableData]; // Crear copia para evitar mutaciones

        // Pre-calcular diasQueFaltan para todas las filas antes de filtrar
        // Esto evita cálculos duplicados y asegura consistencia
        filtered = filtered.map(row => {
            if (!row) return null;
            return {
                ...row,
                diasQueFaltan: calcularDiasQueFaltan(row.time_extension, row.fecha_arribo)
            };
        }).filter(row => row !== null); // Filtrar nulos

        // Filter by "A Cargo" if provided
        if (aCargoFilter && aCargoFilter !== "all" && aCargoFilter !== null) {
            filtered = filtered.filter((row) => {
                if (!row) return false;
                return row.a_cargo === aCargoFilter;
            });
        }

        // Filter by "My Cases" if enabled
        if (myCasesOnly) {
            filtered = filtered.filter((row) => {
                if (!row) return false;
                // For admin users, show all cases
                if (adminView(profile?.profile_id)) {
                    return true;
                }
                // For regular users, show only their cases (matching initials in a_cargo field)
                if (profile?.first_name && profile?.last_name && row.a_cargo) {
                    const userInitials = `${profile.first_name.charAt(0).toUpperCase()}${profile.last_name.charAt(0).toUpperCase()}`;
                    return row.a_cargo === userInitials;
                }
                return false;
            });
        }

        if (filterValue && filterValue.trim().length > 0) {
            // Separar múltiples criterios por coma y limpiar espacios
            const searchTerms = filterValue
                .split(',')
                .map(term => term.trim())
                .filter(term => term.length > 0);

            // Solo aplicar filtro si hay al menos un término válido
            if (searchTerms.length > 0) {
                filtered = filtered.filter((row) => {
                    if (!row) return false;

                    // Función helper para verificar si un término coincide con la fila
                    const termMatches = (searchTerm) => {
                        const lowerCaseFilter = searchTerm.toLowerCase();
                        const normalizedFilter = lowerCaseFilter.replace(/-/g, "/");

                        // Comparación especial para rsk_ref permitiendo '-' como '/'
                        const rsk = (row.rsk_ref || "").toString().toLowerCase();
                        const rskNormalized = rsk.replace(/-/g, "/");
                        const matchesRsk = rsk.includes(normalizedFilter) || rskNormalized.includes(normalizedFilter);

                        if (matchesRsk) return true;

                        // Comparación estándar para el resto de campos
                        // Usar diasQueFaltan ya pre-calculado
                        const searchFields = [
                            row.client_name,
                            row.status,
                            row.a_cargo,
                            row.vessel,
                            row.bl_awb,
                            row.container_number,
                            row.carrier_name,
                            row.claimamount?.toString(),
                            row.ofertas_de_cierre?.toString(),
                            row.recuperado_usd?.toString(),
                            row.fecha_finiquito ? format(new Date(row.fecha_finiquito), "dd/MM/yyyy") : "",
                            ...(adminView(profile?.profile_id) ? [
                                row.exporter_name || "",
                                (row.diasQueFaltan !== null && row.diasQueFaltan !== undefined)
                                    ? String(row.diasQueFaltan)
                                    : ""
                            ] : []),
                        ];

                        return searchFields.some((value) => {
                            if (value === null || value === undefined || value === "") return false;
                            try {
                                return String(value).toLowerCase().includes(lowerCaseFilter);
                            } catch (e) {
                                return false;
                            }
                        });
                    };

                    // Modo OR: si alguno de los términos coincide
                    // Modo AND: todos los términos deben coincidir
                    if (filterMode === "AND") {
                        return searchTerms.every(term => termMatches(term));
                    } else {
                        return searchTerms.some(term => termMatches(term));
                    }
                });
            }
        }

        return filtered;

    }, [tableData, filterValue, myCasesOnly, profile, filterMode, aCargoFilter]);

    // calculate the total claim amount from filtered data
    const totalClaimAmount = useMemo(() => {
        return filteredData.reduce((sum, row) => {
            const amount = row.claimamount;
            // Convertir a número si es string, manejar null/undefined
            const numAmount = typeof amount === 'string'
                ? parseFloat(amount.replace(/[^0-9.-]/g, '')) || 0
                : (typeof amount === 'number' ? amount : 0);
            return sum + numAmount;
        }, 0);
    }, [filteredData]);

    // Sort all filtered data based on sortDescriptor
    const sortedData = useMemo(() => {
        if (sortDescriptor.column === "time_bar") {
            return [...filteredData].sort((a, b) => {
                const diasA = a.diasQueFaltan;
                const diasB = b.diasQueFaltan;

                // Manejar casos especiales - "N/A" y "Expired" van al final
                if (diasA === "N/A" && diasB === "N/A") return 0;
                if (diasA === "N/A") return 1; // N/A siempre va al final
                if (diasB === "N/A") return -1;

                if (diasA === "Expired" && diasB === "Expired") return 0;
                if (diasA === "Expired") return 1; // Expired va después de números pero antes que N/A
                if (diasB === "Expired") return -1;

                // Comparar como números
                const numA = parseInt(diasA);
                const numB = parseInt(diasB);

                // Verificar que los números sean válidos
                if (isNaN(numA) || isNaN(numB)) {
                    return 0;
                }

                const cmp = numA - numB;
                return sortDescriptor.direction === "descending" ? -cmp : cmp;
            });
        }

        return [...filteredData].sort((a, b) => {
            const first = a[sortDescriptor.column];
            const second = b[sortDescriptor.column];
            const cmp = first < second ? -1 : first > second ? 1 : 0;
            return sortDescriptor.direction === "descending" ? -cmp : cmp;
        });
    }, [sortDescriptor, filteredData]);

    // Calculate pagination
    const pages = Math.ceil(sortedData.length / rowsPerPage) || 1;

    const items = useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        const end = start + rowsPerPage;
        return sortedData.slice(start, end);
    }, [page, sortedData, rowsPerPage]);

    const closeMaestraModal = () => {
        const currentRskRef = maestraData?.rsk_ref;
        setIsMaestraModalOpen(false);
        setMaestraData(null);
        setMaestraQvcId(null);
        setSelectedTab("maestra"); // Resetea el tab al cerrar
        // Actualizar solo la fila del caso modificado
        if (currentRskRef && onUpdateSingleCase) {
            onUpdateSingleCase(currentRskRef);
        } else if (onCasesDataReload) {
            // Fallback: recargar todos los datos si no hay rsk_ref
            onCasesDataReload();
        }
    }

    // QuickView modal handlers
    const openQuickView = (rskRef) => {
        setSelectedRskRef(rskRef);
        setIsQuickViewModalOpen(true);
    };

    const closeQuickViewModal = () => {
        setIsQuickViewModalOpen(false);
        setSelectedRskRef(null);
    };

    // Document modal handlers
    const closeDocumentModal = () => {
        setIsDocumentModalOpen(false);
        setDocxBlob(null);
        setPdfBlob(null);
        setDocumentFilename("");
    };

    // Download the generated PDF
    const handleDownload = () => {
        if (pdfBlob && documentFilename) {
            const pdfFilename = documentFilename.replace(/\.docx$/, '.pdf');
            saveAs(pdfBlob, pdfFilename);
        }
    };

    const handleEditMaster = async (id, qvcId) => {
        if (!id || maestraLoading) {
            alert('operacion no valida!')
            return
        }

        setMaestraLoading(true)

        try {
            const response = await apiClientExporter.get(`/exporter/maestra?id=${id}`);
            const data = response.data;
            setMaestraData(data)
            setMaestraQvcId(qvcId) // Guardar el qvc_id del row
            console.log("datamodal", data);
            console.log("qvcId del row:", qvcId);
            setIsMaestraModalOpen(true);
        } catch (err) {
            console.error(err.message);
        } finally {
            console.log("Finalizando editar Maestra");
            setMaestraLoading(false)
        }
    };

    return (
        <>
            <style>{`
                @keyframes slowPulse {
                    0%, 100% {
                        opacity: 1;
                    }
                    50% {
                        opacity: 0.3;
                    }
                }
                .email-blink {
                    animation: slowPulse 3s ease-in-out infinite;
                }
                .sticky-header thead {
                    position: sticky;
                    top: 0;
                    z-index: 10;
                    background-color: white;
                }
                .dark .sticky-header thead {
                    background-color: rgb(17, 24, 39);
                }
            `}</style>
            <Table
                aria-label="Claims details table"
                isStriped
                isVirtualized
                bottomContent={bottomContent}
                bottomContentPlacement="outside"
                topContent={topContent}
                topContentPlacement="outside"
                sortDescriptor={sortDescriptor}
                onSortChange={setSortDescriptor}
                classNames={{
                    wrapper: "h-[calc(100vh-226px)] sticky-header",
                }}
            >
                <TableHeader columns={columns}>
                    {(column) => (
                        <TableColumn
                            key={column.uid}
                            allowsSorting={column.sortable}
                        >
                            {column.uid === "ofertas_de_cierre" ? (
                                <div className="flex flex-col">
                                    <span>Offer</span>
                                    <span className="text-xs text-gray-500">USD</span>
                                </div>
                            ) : (
                                column.name
                            )}
                        </TableColumn>
                    )}
                </TableHeader>
                <TableBody
                    emptyContent={filterValue ? "No results found" : "No data available"}
                    items={items || []}
                >
                    {(row) => (
                        <TableRow
                            key={row?.id || Math.random()}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={getRowBackgroundColor(row)}
                        >
                            {columns.map((column) => (
                                <TableCell key={column.uid}>
                                    {column.uid === "rsk_ref" ? (
                                        <div className="flex items-center gap-2">
                                            <span>{row?.[column.uid] || "N/A"}</span>
                                            {shouldHighlightNewEmail(row) && (
                                                <Icon
                                                    icon="mdi:email"
                                                    className="text-blue-600 email-blink"
                                                    width={18}
                                                    height={18}
                                                />
                                            )}
                                        </div>
                                    ) : column.uid === "client_name" ? (
                                        row.client_name === "DIRECTO" ? row.exporter_name : (row.client_name || "")
                                    ) : column.uid === "claimamount" ? (
                                        `$${new Intl.NumberFormat("es-CL", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        }).format(row?.claimamount || 0)}`
                                    ) : column.uid === "ofertas_de_cierre" ? (
                                        (() => {
                                            const ofertas = row?.ofertas_de_cierre || 0;
                                            const recuperado = row?.recuperado_usd || 0;
                                            const hasOffer = row?.has_offer_flag && ofertas === 0 && recuperado === 0;
                                            return (
                                                <div className="flex flex-col">
                                                    {ofertas > 0 ? (
                                                        <span>
                                                            ${new Intl.NumberFormat("es-CL", {
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: 2,
                                                            }).format(ofertas)}
                                                        </span>
                                                    ) : hasOffer ? (
                                                        <span className="text-sm text-green-600 font-semibold">
                                                            (offer)
                                                        </span>
                                                    ) : (
                                                        <span>N/A</span>
                                                    )}
                                                </div>
                                            );
                                        })()
                                    ) : column.uid === "recuperado_usd" ? (
                                        (() => {
                                            const recuperado = row?.recuperado_usd || 0;
                                            const reclamo = row?.claimamount || 0;
                                            const porcentaje = reclamo > 0
                                                ? ((recuperado / reclamo) * 100).toFixed(2)
                                                : "0.00";
                                            return (
                                                <div className="flex flex-col">
                                                    <span>
                                                        {recuperado > 0
                                                            ? `$${new Intl.NumberFormat("es-CL", {
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: 2,
                                                            }).format(recuperado)}`
                                                            : "N/A"
                                                        }
                                                    </span>
                                                    {recuperado > 0 && (
                                                        <span className="text-xs text-default-500">
                                                            ({porcentaje}%)
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        })()
                                    ) : column.uid === "fecha_finiquito" ? (
                                        row?.fecha_finiquito
                                            ? format(new Date(row.fecha_finiquito), "dd/MM/yyyy")
                                            : "N/A"
                                    ) : column.uid === "actions" ? (
                                        <Chip
                                            color="primary"
                                            size="sm"
                                            isDisabled={maestraLoading}
                                            onClick={() => handleEditMaster(row.id, row.qvc_id)}
                                            role="button"
                                            className="cursor-pointer hover:bg-opacity-80 active:scale-95 transition-all"
                                            aria-label={`View details for ${row?.rsk_ref || "unknown"}`}
                                        >
                                            Open
                                        </Chip>
                                    ) : column.uid === "time_bar" ? (
                                          <span className={getDiasColor(row?.diasQueFaltan)}>
                                              {row?.diasQueFaltan || "N/A"}
                                          </span>
                                    ) : (
                                        row?.[column.uid] || "N/A"
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    )}
                </TableBody>
            </Table>

            {/* Maestra Edit Modal */}
            <Modal
                isOpen={isMaestraModalOpen}
                onClose={closeMaestraModal}
                size="full"
                className="max-h-screen overflow-y-auto"
                scrollBehavior="inside"
                key={isMaestraModalOpen} // Solo remount al abrir/cerrar
            >
                <ModalContent>
                    <>
                        <ModalHeader>
                            <div className="flex flex-col items-center w-full gap-0">
                                <div className="flex justify-center">
                                    <Tabs
                                        aria-label="Opciones de Planilla"
                                        selectedKey={selectedTab}
                                        onSelectionChange={setSelectedTab}
                                    >
                                        <Tab key="maestra" title="Extra Judicial" />
                                        <Tab key="judicial" title="Judicial" />
                                        <Tab
                                            key="quickview"
                                            title="QuickView"
                                            isDisabled={!maestraQvcId}
                                            className={!maestraQvcId ? "opacity-50 text-gray-400" : ""}
                                        />
                                        <Tab
                                            key="claimletter"
                                            title="Claim Letter"
                                            isDisabled={!maestraQvcId}
                                            className={!maestraQvcId ? "opacity-50 text-gray-400" : ""}
                                        />
                                        <Tab
                                            key="auditlog"
                                            title={<span className="flex items-center gap-1"><Icon icon="mdi:history" width={18} height={18} /> History</span>}
                                        />
                                    </Tabs>
                                </div>
                                <div className="text-sm font-bold text-center items-center">
                                    {maestraData && (
                                        <span className="ml-2">
                                            {generateCopiableName(maestraData.rsk_ref, maestraData.embarcador, maestraData.nave, maestraData.voyage_number, maestraData.bl_awb, maestraData.container, maestraData.puerto_origen, maestraData.puerto_destino, maestraData.reclamo_usd, maestraData.reclamo_euros, maestraData.quickview_claim_survey?.selectedcurrency)}
                                        </span>
                                    )}
                                    {maestraData && (
                                        <button
                                            onClick={() => {
                                                const textToCopy = generateCopiableName(maestraData.rsk_ref, maestraData.embarcador, maestraData.nave, maestraData.voyage_number, maestraData.bl_awb, maestraData.container, maestraData.puerto_origen, maestraData.puerto_destino, maestraData.reclamo_usd, maestraData.reclamo_euros, maestraData.quickview_claim_survey?.selectedcurrency);
                                                navigator.clipboard.writeText(textToCopy);
                                            }}
                                            className="ml-2 p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors duration-200"
                                            title="Copy to clipboard"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </ModalHeader>
                        <ModalBody>
                            <AnimatePresence>
                                {selectedTab === "maestra" && (
                                    <motion.div
                                        key="maestra"
                                        initial={{ opacity: 0, x: 30 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -30 }}
                                        transition={{ duration: 0.25 }}
                                    >
                                        <DetalleMaestraEditar maestraData={maestraData} readOnlyClaims={readOnlyClaims} aCargoOptions={aCargoOptions} />
                                    </motion.div>
                                )}
                                {selectedTab === "judicial" && (
                                    <motion.div
                                        key="judicial"
                                        initial={{ opacity: 0, x: 30 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -30 }}
                                        transition={{ duration: 0.25 }}
                                    >
                                        <Card><CardBody><DetalleJudicialEditar judicialData={maestraData} readOnlyClaims={readOnlyClaims} /></CardBody></Card>
                                    </motion.div>
                                )}
                                {selectedTab === "quickview" && (
                                    <motion.div
                                        key="quickview"
                                        initial={{ opacity: 0, x: 30 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -30 }}
                                        transition={{ duration: 0.25 }}
                                    >
                                        <DetalleQuickview
                                            rskRef={maestraData?.rsk_ref}
                                            claimId={maestraData?.id}
                                            isBusy={false}
                                            setIsBusy={() => {}}
                                            readOnlyClaims={readOnlyClaims}
                                        />
                                    </motion.div>
                                )}
                                {selectedTab === "claimletter" && (
                                    <motion.div
                                        key="claimletter"
                                        initial={{ opacity: 0, x: 30 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -30 }}
                                        transition={{ duration: 0.25 }}
                                    >
                                        <ClaimLetterTab maestraData={maestraData} readOnlyClaims={readOnlyClaims} />
                                    </motion.div>
                                )}
                                {selectedTab === "auditlog" && (
                                    <motion.div
                                        key="auditlog"
                                        initial={{ opacity: 0, x: 30 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -30 }}
                                        transition={{ duration: 0.25 }}
                                    >
                                        <AuditLogTab maestraData={maestraData} />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </ModalBody>
                        <ModalFooter>
                            <Button color="danger" variant="light" onPress={closeMaestraModal}>
                                Close
                            </Button>
                        </ModalFooter>
                    </>
                </ModalContent>
            </Modal>

            {/* QuickView Modal */}
            <Modal
                isOpen={isQuickViewModalOpen}
                onClose={closeQuickViewModal}
                size="full"
                className="max-h-screen overflow-y-auto"
                scrollBehavior="inside"
            >
                <ModalContent>
                    {() => (
                        <>
                            <ModalHeader className="flex flex-col gap-1">QuickView</ModalHeader>
                            <ModalBody>
                                <DetalleQuickviewExporter rskRef={selectedRskRef} readOnlyClaims={readOnlyClaims} />
                            </ModalBody>
                            <ModalFooter>
                                <Button color="danger" variant="light" onPress={closeQuickViewModal}>
                                    Close
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>

            {/* Document Preview Modal */}
            <Modal
                isOpen={isDocumentModalOpen}
                onClose={closeDocumentModal}
                size="5xl"
                className="max-h-screen"
            >
                <ModalContent>
                    {() => (
                        <>
                            <ModalHeader className="flex flex-col gap-1">Document Preview</ModalHeader>
                            <ModalBody>
                                {pdfBlob ? (
                                    <iframe
                                        src={URL.createObjectURL(pdfBlob)}
                                        width="100%"
                                        height="600px"
                                        title="Document Preview"
                                        style={{ border: "1px solid #ccc" }}
                                    />
                                ) : (
                                    <div className="text-center">
                                        <p>Loading preview...</p>
                                    </div>
                                )}
                            </ModalBody>
                            <ModalFooter>
                                <Button color="danger" variant="light" onPress={closeDocumentModal}>
                                    Close
                                </Button>
                                <Button color="primary" onPress={handleDownload}>
                                    Download Document (.pdf)
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>
        </>
    );
}

export default ClaimsTable;
